import { Fragment, useMemo } from 'react';
import type { FieldSchema } from '../types';
import type { Step } from '../models/Step';
import { useFormContext } from '../context/FormContext';
import { collectFieldSchemas, resolveDependantOptions } from '../services/fieldSchemaService';

export function Summary({
  showReference = true,
  fieldSchemas = {},
}: {
  showReference?: boolean;
  fieldSchemas?: Record<string, FieldSchema>;
}) {
  const { formService, displayService, stateService, scrollService, schema, forceUpdate } =
    useFormContext();
  const form = formService.getFormData();

  const schemaFieldSchemas = useMemo<Record<string, FieldSchema>>(
    () => collectFieldSchemas(schema),
    [schema]
  );

  /**
   * Without a field schema an answer renders as its raw stored value ('1',
   * 'grade-b-quarry-manager') instead of the option label the user picked, so
   * fall back to the form schema when the caller hasn't passed one in.
   */
  const fieldSchemaFor = (questionId: string): FieldSchema | undefined =>
    resolveDependantOptions(
      fieldSchemas[questionId] ?? schemaFieldSchemas[questionId],
      formService
    );

  // The final step is the review itself, which never gets summarised or edited.
  const reviewableSteps: Step[] = form.steps.filter((step) => !formService.isFinalStep(step));

  const setActiveStep = (stepId: string): void => {
    const step = form.steps.find((s) => s.id === stepId);

    if (!step) {
      return;
    }

    // Editing always jumps backwards, so clear any validation state from the
    // step we're leaving, and re-render — setActiveStep only mutates the
    // service, so without forceUpdate the review screen stays on screen.
    stateService.showErrors = false;
    formService.setActiveStep(step);
    scrollService.stepScrollToTop();
    forceUpdate();
  };

  return (
    <div>
      {stateService.submitErrorMsg && (
        <p className="validation-msg">{stateService.submitErrorMsg}</p>
      )}

      <div className="review">
        {reviewableSteps.map((step, stepIndex) => {
          const hasViewableQuestions = step.hasViewableQuestions(displayService);
          const visibleSections = step.sections.filter((section) =>
            displayService.showSection(section)
          );

          return (
            <Fragment key={step.id}>
              {stepIndex > 0 && <hr className="space-xs" />}

              <div className="review__group">
                <div className="review__heading">
                  <h4>
                    {step.label}
                    {step.title && <span> - {step.title}</span>}
                  </h4>

                  {hasViewableQuestions && (
                    <button
                      type="button"
                      onClick={() => setActiveStep(step.id)}
                      className="button as-link edit"
                    >
                      Edit this section
                    </button>
                  )}
                </div>

                <hr className="thin space-xs" />

                {visibleSections.map((section, sectionIndex) => (
                    <div key={section.id}>
                      {/* The heading already has a rule under it, so only divide section from section */}
                      {sectionIndex > 0 && <hr className="thin" />}
                      {section.label && <h5 className="h2">{section.label}</h5>}
                      {section.groups.map((group) =>
                        displayService.showGroup(group) ? (
                          <div key={`${group.id}-${group.index}`}>
                            {group.label && <h5 className="h2">{group.label}</h5>}

                            {group.questions.map((question) =>
                              displayService.showQuestion(question, group) &&
                              question.hasPopulatedAnswer() ? (
                                <div className="review__answer" key={`${question.id}-${group.index}`}>
                                  {question.type !== 'FileUpload' ? (
                                    <div>
                                      <strong className="review__title">{question.label}</strong>
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html: question.getNiceAnswer(fieldSchemaFor(question.id)),
                                        }}
                                      />
                                      <br />
                                    </div>
                                  ) : (
                                    <div>
                                      <strong className="review__title">{question.label}</strong>
                                      <ul>
                                        {(question.answer as Array<{ originalFileName?: string; formattedFileSize?: string }>).map(
                                          (fileUpload, i) => (
                                            <li key={i}>
                                              {fileUpload.originalFileName} ({fileUpload.formattedFileSize})
                                            </li>
                                          )
                                        )}
                                      </ul>
                                      <br />
                                    </div>
                                  )}
                                </div>
                              ) : null
                            )}
                          </div>
                        ) : null
                      )}
                    </div>
                  ))}

                {!hasViewableQuestions && <p>No questions required for this step.</p>}
              </div>
            </Fragment>
          );
        })}

        <div>
          <hr className="pull-right" />
          {showReference && (
            <div>
              <strong>Reference: </strong>
              {formService.getFormReference()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
